/**
 * TextInput — primitive de champ texte.
 *
 * @remarks
 * ### `size` est réservé
 *
 * L'attribut natif `size` d'un `<input>` fixe sa largeur en caractères —
 * personne ne l'utilise ici, et ce n'est pas un levier que le DS veut
 * proposer. Le nom sert donc à l'échelle du composant, comme sur `Button` et
 * `Select`.
 *
 * `sm` existe pour les panneaux compacts (barres de filtres), où un champ de
 * recherche voisine un `Select size="sm"` : les deux doivent s'aligner
 * (SAAS-909).
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

type TextInputSize = 'sm' | 'md';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  error?: boolean;
  size?: TextInputSize;
}

const PICKER_TYPES = ['date', 'datetime-local', 'time', 'month', 'week'];

const SIZES: Record<TextInputSize, string> = {
  sm: 'h-8 px-2 text-xs',
  md: 'h-9 px-3 py-1 text-sm',
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { className, type = 'text', error = false, size = 'md', onClick, ...rest },
  ref,
) {
  const hasPicker = PICKER_TYPES.includes(type);

  return (
    <input
      ref={ref}
      type={type}
      onClick={(event) => {
        if (hasPicker) {
          // Open the native picker on a click anywhere in the field, not just the icon.
          try {
            event.currentTarget.showPicker();
          } catch {
            // showPicker can throw (e.g. unsupported / cross-origin) — fall back to default.
          }
        }
        onClick?.(event);
      }}
      className={cn(
        'bg-surface flex w-full rounded-md border transition-colors',
        'placeholder:text-fg-subtle',
        'focus-visible:outline-primary-500 focus-visible:outline-2 focus-visible:outline-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
        error ? 'border-error' : 'border-border-strong hover:border-border',
        SIZES[size],
        hasPicker &&
          'relative cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
        className,
      )}
      {...rest}
    />
  );
});
